// React hook that owns the Web photo upload queue.
//
// Phase 1: serial execution (one photo at a time). Concurrency is
// intentionally omitted so the controller is easy to test and
// reason about; the audit's Phase 2 plan introduces a 3-way pool.
//
// Idempotency contract:
//   - One `clientPhotoId` per pick (generated via `crypto.randomUUID()`
//     inside `enqueue`). Reused for every retry of the SAME pick.
//   - The Backend dedupes on `(report_id, client_photo_id)` so
//     retries always reuse the same server-side `photo_id` + `r2_key`.
//   - The user picks the same File twice → two distinct
//     `clientPhotoId`s → two distinct photo rows. This matches
//     Mobile's behavior (Mobile's `addLocalPhoto` calls
//     `Uuid().v4()` per pick).
//
// Retry contract:
//   - `failed` tasks expose a `retry(taskId)` callback that
//     re-runs the four-step pipeline with the SAME `clientPhotoId`,
//     `file`, and `thumbnail`. The Backend's presign dedup-reuse
//     path ensures no orphan D1 row.
//   - Spam-clicking Retry is single-flighted: while a retry is in
//     flight, further Retry calls are ignored.
//
// UI integration:
//   - `inFlightCount` is the single source of truth for the Submit
//     gate (the editor disables Submit when > 0).
//   - `errorBanner` carries the most recent validation failure so
//     the UI can render a single error line.

import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import { type AllowedPhotoMimeType, ALLOWED_PHOTO_MIME_TYPES } from '@/api/reportsApi';
import type { ReportPhotoJson } from '@/api/types';
import { generateThumbnail } from './photoThumbnail';
import { PhotoUploadPipelineError, runUploadTask, validateFile } from './photoUploadService';
import type { UploadTask } from './photoUploadTypes';

export interface UsePhotoUploadController {
  tasks: UploadTask[];
  inFlightCount: number;
  errorBanner: string | null;
  enqueue: (files: FileList | File[]) => void;
  retry: (taskId: string) => void;
  removeTask: (taskId: string) => void;
  hasPendingOrInFlight: boolean;
}

export interface UsePhotoUploadArgs {
  reportId: string | null;
  getToken: () => string | null;
  /**
   * Optional callback invoked once per task immediately after the server
   * confirms the upload via finalize. Fires synchronously inside
   * drainTask, BEFORE any state dispatch, so the caller can atomically
   * add the server photo to committed state before (or alongside) the
   * task being removed from the queue.
   *
   * This replaces the old pattern where the caller watched
   * `tasks.filter(t => t.status === 'uploaded')` and fired a
   * GET /photos round-trip. Using this callback avoids the double-
   * render window where both CommittedTile and TaskTile appear for
   * the same photo.
   */
  onServerPhoto?: (serverPhoto: ReportPhotoJson) => void;
}

function isAllowedMime(mime: string): mime is AllowedPhotoMimeType {
  return (ALLOWED_PHOTO_MIME_TYPES as readonly string[]).includes(mime.toLowerCase());
}

type Action =
  | { type: 'enqueue'; tasks: UploadTask[]; errorBanner: string | null }
  | { type: 'update'; taskId: string; patch: Partial<UploadTask> }
  | { type: 'retry'; taskId: string }
  | { type: 'remove'; taskId: string }
  | { type: 'setErrorBanner'; message: string | null };

export function reducer(
  state: { tasks: UploadTask[]; errorBanner: string | null },
  action: Action,
) {
  switch (action.type) {
    case 'enqueue':
      return {
        tasks: [...state.tasks, ...action.tasks],
        errorBanner: action.errorBanner,
      };
    case 'update':
      return {
        ...state,
        tasks: state.tasks.map((t) => (t.taskId === action.taskId ? { ...t, ...action.patch } : t)),
      };
    case 'retry':
      return {
        ...state,
        tasks: state.tasks.map((t) =>
          t.taskId === action.taskId && t.status === 'failed'
            ? { ...t, status: 'pending' as const, error: null, attempts: t.attempts + 1 }
            : t,
        ),
      };
    case 'remove':
      return { ...state, tasks: state.tasks.filter((t) => t.taskId !== action.taskId) };
    case 'setErrorBanner':
      return { ...state, errorBanner: action.message };
    default:
      return state;
  }
}

/**
 * Hook that owns the upload queue for a single report. The editor
 * calls `enqueue` from the hidden file input's `onChange`. Tasks are
 * drained serially via an internal effect.
 */
export function usePhotoUploadController(args: UsePhotoUploadArgs): UsePhotoUploadController {
  const { reportId, getToken, onServerPhoto } = args;
  const [state, dispatch] = useReducer(reducer, { tasks: [], errorBanner: null });
  const tasksRef = useRef<UploadTask[]>([]);
  tasksRef.current = state.tasks;

  // Stable ref for the optional callback so the drain effect does not
  // need onServerPhoto in its dependency array. The callback identity is
  // stable because ReportEditorScreen defines it inline once per render
  // and the ref always points to the latest version.
  const onServerPhotoRef = useRef(onServerPhoto);
  onServerPhotoRef.current = onServerPhoto;

  // Serial queue: at most one task in 'uploading' at any time.
  // The effect re-fires whenever (a) a new task lands, (b) the
  // current task transitions to 'uploaded' or 'failed', or (c)
  // reportId appears (a freshly-created draft).
  const isDrainingRef = useRef(false);
  useEffect(() => {
    if (!reportId) return;
    if (isDrainingRef.current) return;
    const next = state.tasks.find((t) => t.status === 'pending');
    if (!next) return;
    isDrainingRef.current = true;
    void drainTask({
      taskId: next.taskId,
      reportId,
      getToken,
      getTask: (id) => tasksRef.current.find((t) => t.taskId === id) ?? null,
      patch: (id, p) => dispatch({ type: 'update', taskId: id, patch: p }),
      remove: (id) => dispatch({ type: 'remove', taskId: id }),
      onServerPhoto: (serverPhoto) => onServerPhotoRef.current?.(serverPhoto),
      isDrainingRef,
    });
  }, [state.tasks, reportId, getToken]);

  const enqueue = useCallback(
    (files: FileList | File[]) => {
      if (!reportId) {
        dispatch({
          type: 'setErrorBanner',
          message: 'Cannot upload: report is not ready yet.',
        });
        return;
      }
      const list = Array.from(files);
      if (list.length === 0) return;

      const rejected: string[] = [];
      const newTasks: UploadTask[] = [];
      for (const file of list) {
        const v = validateFile(file);
        if (!v.ok) {
          rejected.push(`${file.name}: ${v.message ?? 'unsupported file'}`);
          continue;
        }
        const mime = file.type.toLowerCase();
        // `validateFile` already enforces the allowlist, but TS needs the guard.
        if (!isAllowedMime(mime)) continue;
        newTasks.push({
          taskId: crypto.randomUUID(),
          clientPhotoId: crypto.randomUUID(),
          file,
          mimeType: mime,
          sizeBytes: file.size,
          thumbnail: null,
          status: 'pending',
          error: null,
          photoId: null,
          attempts: 1,
          createdAt: Date.now(),
        });
      }
      const banner = rejected.length > 0 ? rejected.join('\n') : null;
      dispatch({ type: 'enqueue', tasks: newTasks, errorBanner: banner });
    },
    [reportId],
  );

  const retry = useCallback((taskId: string) => {
    dispatch({ type: 'setErrorBanner', message: null });
    dispatch({ type: 'retry', taskId });
  }, []);

  const removeTask = useCallback((taskId: string) => {
    dispatch({ type: 'remove', taskId });
  }, []);

  const inFlightCount = state.tasks.filter(
    (t) => t.status === 'pending' || t.status === 'uploading',
  ).length;

  return {
    tasks: state.tasks,
    inFlightCount,
    errorBanner: state.errorBanner,
    enqueue,
    retry,
    removeTask,
    hasPendingOrInFlight: inFlightCount > 0,
  };
}

interface DrainTaskArgs {
  taskId: string;
  reportId: string;
  getToken: () => string | null;
  getTask: (taskId: string) => UploadTask | null;
  patch: (taskId: string, patch: Partial<UploadTask>) => void;
  remove: (taskId: string) => void;
  /**
   * Called exactly once on success, after the server photo has been
   * confirmed via finalize, but BEFORE the task is removed from the
   * queue. The caller can use this to atomically add the server photo
   * to committed state while the task still exists in `tasks`, then
   * the task is removed — preventing the double-render window where
   * both CommittedTile and TaskTile appear for the same photo.
   *
   * Important: this is a synchronous callback, not an async promise.
   * It fires before any state dispatch so the caller can update its
   * own committed-photos state without a separate GET round-trip.
   */
  onServerPhoto: (serverPhoto: ReportPhotoJson) => void;
  /** Ref to the draining flag. Set to false in drainTask's own finally block so the next task starts only after the current one is fully done. */
  isDrainingRef: React.MutableRefObject<boolean>;
}

/**
 * Single-shot drain of one task. Generates thumbnail (best-effort),
 * transitions to `uploading`, calls `runUploadTask`, and either
 * transitions to `uploaded` or `failed` based on the outcome.
 *
 * The function is module-private and side-effect-only (it mutates
 * state via the `patch` callback). The controller hook above owns
 * the closure that decides which task to drain next.
 */
async function drainTask(args: DrainTaskArgs): Promise<void> {
  const { taskId, reportId, getToken, getTask, patch, remove, onServerPhoto, isDrainingRef } = args;
  const before = getTask(taskId);
  if (!before) return;
  if (before.status !== 'pending') return;

  // Generate thumbnail (best-effort). Failure is non-fatal: the
  // service runs the four-step pipeline without `thumbnail_bytes`
  // when `thumbnail === null`.
  let thumbnail = before.thumbnail;
  if (thumbnail === null) {
    try {
      const t = await generateThumbnail(before.file);
      if (t) thumbnail = t;
    } catch {
      // Already null — service handles the fallback.
    }
  }

  // Mark uploading. We snapshot the freshly-generated thumbnail
  // into the persisted task so a retry uses the same JPEG (instead
  // of re-decoding and potentially producing a different bitmap).
  patch(taskId, { status: 'uploading', error: null, thumbnail });

  try {
    const serverPhoto = await runUploadTask({ ...before, thumbnail }, { reportId, getToken });
    // Fire the callback BEFORE any state dispatch so the caller can
    // update its committed-photos atomically with (or before) the
    // task removal. This prevents the double-render window where
    // both CommittedTile and TaskTile appear for the same photo.
    onServerPhoto(serverPhoto);
    patch(taskId, { status: 'uploaded', error: null });
    remove(taskId);
  } catch (err) {
    const msg =
      err instanceof PhotoUploadPipelineError
        ? err.message
        : err instanceof Error
          ? err.message
          : 'Upload failed';
    patch(taskId, { status: 'failed', error: msg });
  } finally {
    // Reset the draining flag ONLY after the ENTIRE task is done
    // (including thumbnail generation and runUploadTask). This is
    // inside drainTask's own finally block — NOT attached to the
    // outer void drainTask(...) call — so it fires at the correct
    // time: after all async work, not after the first await.
    isDrainingRef.current = false;
  }
}
